<template>
  <div class="edit-table-wrapper">
    <!-- Header with Sticky Behavior -->
    <div class="header-section">
      <div class="header-content">
        <div class="header-left">
          <v-btn icon @click="backToTableData" class="back-btn" color="#0d2c6d">
            <v-icon>mdi-arrow-right</v-icon>
          </v-btn>

          <!-- Editable Table Name with Improved Styling -->
          <div class="table-name-section">
            <v-text-field v-model="tableName.hebrew_name" :rules="[v => !!v || 'שם הטבלה הוא שדה חובה']" hide-details
              outlined dense class="table-name-input" placeholder="שם הטבלה">
              <template v-slot:prepend-inner>
                <v-icon color="#0d2c6d" small>mdi-table-edit</v-icon>
              </template>
            </v-text-field>
          </div>
        </div>

        <div class="header-right">
          <div class="api-name-section">
            <span class="api-label">API Table Name:</span>
            <div class="api-value">
              {{ tableName.name }}
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-btn icon x-small v-on="on" @click="copyToClipboard(tableName.name)" class="copy-btn">
                    <v-icon x-small>mdi-content-copy</v-icon>
                  </v-btn>
                </template>
                <span>העתק שם API</span>
              </v-tooltip>
            </div>
          </div>

          <div class="api-name2-section">
            <span class="api-label">API Workspace ID:</span>
            <div class="api-value">
              {{ workspaceId }}
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-btn icon x-small v-on="on" @click="copyToClipboard(workspaceId)" class="copy-btn">
                    <v-icon x-small>mdi-content-copy</v-icon>
                  </v-btn>
                </template>
                <span>העתק ID</span>
              </v-tooltip>
            </div>
          </div>

          <div class="header-actions">
            <v-btn outlined :color="$store.state.colorPrimary" class="action-btn mr-3" @click="exportJson"
              elevation="1">
              <v-icon left>mdi-code-json</v-icon>
              <span class="action-text">ייצוא JSON</span>
            </v-btn>

            <v-btn :loading="progressShow" dark :disabled="!allowSave || !tableName.hebrew_name"
              :color="$store.state.colorPrimary" class="action-btn save-btn" @click="saveChanges" elevation="2">
              <v-icon left>mdi-content-save</v-icon>
              <span class="action-text">שמור שינויים</span>
            </v-btn>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content with Improved Layout -->
    <div class="main-content">
      <!-- Settings Section with Card Design -->
      <section class="settings-section">
        <div class="section-card">
          <div class="settings-header">
            <div class="section-title-wrapper">
              <v-icon color="#0d2c6d" class="section-icon">mdi-cog-outline</v-icon>
              <h2 class="section-title">הגדרות תצוגה</h2>
            </div>
            <v-btn :color="$store.state.colorPrimary" dark small @click="addFiled" class="add-field-btn" elevation="1">
              <v-icon left small>mdi-plus</v-icon>
              הוסף שדה
            </v-btn>
          </div>

          <div class="settings-toggles">
            <div v-for="setting in checkboxSettings" :key="setting.model" class="setting-toggle">
              <v-btn :color="settings[setting.model] ? $store.state.colorPrimary : 'grey lighten-1'"
                :dark="settings[setting.model]" :outlined="!settings[setting.model]"
                @click="settings[setting.model] = !settings[setting.model]" class="toggle-btn" small elevation="1">
                <v-icon left small>{{ getSettingIcon(setting.model) }}</v-icon>
                {{ setting.label }}
              </v-btn>
            </div>
          </div>
        </div>
      </section>

      <!-- Fields Section with Improved Cards -->
      <section class="fields-section">
        <div class="section-card">
          <div class="fields-header">
            <div class="section-title-wrapper">
              <v-icon color="#0d2c6d" class="section-icon">mdi-database-edit-outline</v-icon>
              <h2 class="section-title">שדות הטבלה</h2>
            </div>
            <div class="fields-count">
              <span class="count-label">מספר שדות:</span>
              <span class="count-value">{{ fields.length }}</span>
            </div>
          </div>

          <div class="fields-container">
            <draggable v-model="fields" :handle="'.handle'" ghost-class="drag-ghost" drag-class="drag-active"
              :animation="200" class="fields-draggable">
              <transition-group name="field-list">
                <div v-for="(field, index) in fields" :key="index" class="field-card">
                  <div class="field-header">
                    <div class="field-drag handle">
                      <v-icon small>mdi-drag</v-icon>
                      <span class="field-number">{{ index + 1 }}</span>
                    </div>

                    <div class="field-type-badge" :class="getFieldTypeClass(field.type)">
                      <v-icon small left>{{ getFieldTypeIcon(field.type) }}</v-icon>
                      {{ field.type }}
                    </div>
                  </div>

                  <div class="field-content">
                    <div class="field-main">
                      <v-text-field v-model="field.hebrew_name"
                        :disabled="['date_created', 'date_modified', 'id'].includes(field.name)" hide-details outlined
                        dense placeholder="שם השדה" label="שם השדה" class="field-name-input"></v-text-field>

                      <v-text-field v-model="field.name" @click:append="copyToClipboard(field.name)"
                        append-icon="mdi-content-copy" readonly hide-details outlined dense placeholder="API name"
                        label="API name" class="field-name-input"></v-text-field>

                      <v-select v-model="field.type" :items="fieldType"
                        :disabled="['date_created', 'date_modified', 'id'].includes(field.name)" hide-details dense
                        outlined class="field-type-select" label="סוג שדה">
                        <template v-slot:selection="{ item }">
                          <v-icon left small>{{ getFieldTypeIcon(item) }}</v-icon>
                          {{ item }}
                        </template>
                        <template v-slot:item="{ item }">
                          <v-icon left small>{{ getFieldTypeIcon(item) }}</v-icon>
                          {{ item }}
                        </template>
                      </v-select>
                    </div>

                    <div class="field-actions">
                      <div class="action-group">
                        <v-btn small :color="field.allowNull ? $store.state.colorPrimary : 'grey lighten-1'"
                          :dark="field.allowNull" :outlined="!field.allowNull"
                          @click="field.allowNull = !field.allowNull" class="action-btn"
                          :disabled="['date_created', 'date_modified', 'id'].includes(field.name)" elevation="1">
                          <v-icon left small>mdi-null</v-icon>
                          אפשר ריק
                        </v-btn>

                        <v-btn small :color="field.unique ? $store.state.colorPrimary : 'grey lighten-1'"
                          :outlined="!field.unique" :dark="field.unique" @click="field.unique = !field.unique"
                          class="action-btn" :disabled="['date_created', 'date_modified', 'id'].includes(field.name)"
                          elevation="1">
                          <v-icon left small>mdi-key-variant</v-icon>
                          ייחודי
                        </v-btn>

                        <v-btn small :color="field.show ? $store.state.colorPrimary : 'grey lighten-1'"
                          :outlined="!field.show" :dark="field.show" @click="field.show = !field.show"
                          class="action-btn" elevation="1">
                          <v-icon left small>{{ field.show ? 'mdi-eye' : 'mdi-eye-off' }}</v-icon>
                          {{ field.show ? 'מוצג' : 'מוסתר' }}
                        </v-btn>
                      </div>

                      <!-- Additional Actions with Improved Styling -->
                      <div class="additional-actions">
                        <v-menu v-if="field.type === 'מספר'" offset-y :close-on-content-click="true">
                          <template v-slot:activator="{ on }">
                            <v-btn icon small v-on="on" class="ml-2 currency-btn"
                              :disabled="['id'].includes(field.name)"
                              :color="field.coin ? $store.state.colorPrimary : 'grey'">
                              <v-icon small>{{ getCurrencyIcon(field.coin) }}</v-icon>
                            </v-btn>
                          </template>
                          <v-list dense class="currency-menu">
                            <v-list-item v-for="coin in coins" :key="coin" @click="field.coin = choosedCoin(coin)"
                              :class="{ 'selected-currency': field.coin === choosedCoin(coin) }">
                              <v-list-item-icon>
                                <v-icon small
                                  :color="field.coin === choosedCoin(coin) ? $store.state.colorPrimary : ''">
                                  {{ getCurrencyIcon(coin) }}
                                </v-icon>
                              </v-list-item-icon>
                              <v-list-item-title>{{ coin }}</v-list-item-title>
                            </v-list-item>
                          </v-list>
                        </v-menu>

                        <v-tooltip bottom v-if="field.type === 'רשימה'">
                          <template v-slot:activator="{ on }">
                            <v-btn v-on="on" icon small :color="$store.state.colorPrimary"
                              @click="openListField(field, index)" class="list-edit-btn" elevation="1">
                              <v-icon>mdi-format-list-bulleted</v-icon>
                            </v-btn>
                          </template>
                          <span>ערוך פריטי רשימה</span>
                        </v-tooltip>

                        <v-tooltip bottom v-if="field.type === 'רשימה מקושרת'">
                          <template v-slot:activator="{ on }">
                            <v-btn v-on="on" icon small :color="$store.state.colorPrimary"
                              @click="openListLinkedField(field, index)" class="list-link-btn" elevation="1">
                              <v-icon>mdi-link-variant</v-icon>
                            </v-btn>
                          </template>
                          <span>הגדר קישור לרשימה</span>
                        </v-tooltip>
                      </div>
                    </div>
                  </div>
                </div>
              </transition-group>
            </draggable>
          </div>
        </div>
      </section>
    </div>

    <!-- Floating Save Button for Mobile -->
    <v-btn v-if="allowSave && tableName.hebrew_name" fixed dark :color="$store.state.colorPrimary" bottom right fab
      class="floating-save-btn" @click="saveChanges" :loading="progressShow">
      <v-icon>mdi-content-save</v-icon>
    </v-btn>

    <!-- Dialogs -->
    <ListDialog v-model="listDialog" v-if="listDialog" :field="fieldEditList" :fieldIndex="fieldIndex"
      @listUpdated="listUpdated" />

    <ListLinkedDialog v-model="listLinkedDialog" v-if="listLinkedDialog" :field="fieldEditListLinked"
      :fieldIndex="fieldIndex" @listLinkedUpdated="listLinkedUpdated" />

    <SnackBar v-model="snackbar" :snacbarText="snacbarText" :snackbarColorBt="snackbarColorBt" />
  </div>
</template>



<script>
import ListDialog from '@/components/user/editTable/dialogs/ListDialog'
import ListLinkedDialog from '@/components/user/editTable/dialogs/ListLinkedDialog'
import ApiServices from '@/services/api.service'
import SnackBar from '@/components/widgets/snackBar.vue'
import Auth from '@/services/auth.service.js'
import draggable from 'vuedraggable';

export default {
  components: {
    SnackBar,
    draggable,
    ListDialog,
    ListLinkedDialog
  },
  props: {
    tableName: Object,
    value: { type: Boolean, default: false },
  },
  data: () => ({
    fields: [],
    progressShow: true,
    snackbar: false,
    snackbarColorBt: "green",
    snacbarText: "",
    allowSave: true,
    fieldType: ["טקסט", "מספר", "תאריך", "שעה", "תאריך ושעה", "רשימה", "רשימה מקושרת", "לינק"],
    listDialog: false,
    fieldEditList: {},
    fieldIndex: 0,
    fieldEditListLinked: {},
    listLinkedDialog: false,
    workspaceId: "",
    coins: ['לא נבחר', 'שקל', 'דולר', 'אירו'],
    isMenuOpen: [], // Initially, the menu is closed
    isStyleMenuOpen: false,
    settings: { multiple_selection: true, expended_rows: false, show_copy_icon: false },
    checkboxSettings: [
      {
        model: "multiple_selection",
        label: "אפשר מחיקה מרובה",
      },
      {
        model: "expended_rows",
        label: "הרחב שורות",
      },
      {
        model: "show_copy_icon",
        label: "הצגת אייקון העתק",
      },
    ],
  }),
  computed: {
    openMode: {
      get() {
        return this.value
      },
      set(v) {
        return this.$emit('input', v)
      }
    },
  },
  methods: {
    toggleMenu(index) {
      this.$set(this.isMenuOpen, index, !this.isMenuOpen[index]); // Toggle the menu state using Vue's $set method
    },
    toggleStyleMenu() {
      this.isStyleMenuOpen = !!this.isStyleMenuOpen;
    },
    getCurrencyIcon(currency) {
      switch (currency) {
        case "שקל":
          return "mdi-currency-ils";
        case "דולר":
          return "mdi-currency-usd";
        case "אירו":
          return "mdi-currency-eur";
        case "₪":
          return "₪";
        case "$":
          return "$";
        case "€":
          return "€";
        default:
          return "mdi-hand-coin-outline";
      }
    },
    choosedCoin(item) {
      switch (item) {
        case "שקל":
          return "₪";
        case "דולר":
          return "$";
        case "אירו":
          return "€";
        default:
          return "";
      }
    },
    getFieldTypeClass(type) {
      switch (type) {
        case "טקסט":
          return "type-text"
        case "מספר":
          return "type-number"
        case "תאריך":
          return "type-date"
        case "שעה":
          return "type-time"
        case "תאריך ושעה":
          return "type-datetime"
        case "רשימה":
          return "type-list"
        case "רשימה מקושרת":
          return "type-linked-list"
        case "לינק":
          return "type-link"
        default:
          return "type-text"
      }
    },

    getFieldTypeIcon(type) {
      switch (type) {
        case 'טקסט': return 'mdi-form-textbox'
        case 'מספר': return 'mdi-numeric'
        case 'תאריך': return 'mdi-calendar'
        case 'תאריך ושעה': return 'mdi-calendar-clock'
        case 'שעה': return 'mdi-clock-outline'
        case 'רשימה': return 'mdi-format-list-bulleted'
        case 'רשימה מקושרת': return 'mdi-link-variant'
        case 'לינק': return 'mdi-link'
        default: return 'mdi-form-textbox'
      }
    },
    getSettingIcon(model) {
      switch (model) {
        case 'multiple_selection':
          return 'mdi-checkbox-multiple-marked-outline'
        case 'expended_rows':
          return 'mdi-table-row-height'
        case 'show_copy_icon':
          return 'mdi-content-copy'
        default:
          return 'mdi-cog'
      }
    },
    handleFieldTypeAction(field, index) {
      if (field.type === 'רשימה') {
        this.openListField(field, index)
      } else if (field.type === 'רשימה מקושרת') {
        this.openListLinkedField(field, index)
      } else if (field.type === 'מספר') {
        this.toggleMenu(index)
      }
    },
    copyToClipboard(text) {
      console.log(121111);

      navigator.clipboard.writeText(text)
        .then(() => {
          this.showSnackBar("הועתק " + text, "green");
          // You can optionally show a success message to the user here
        })
        .catch((error) => {
          console.error('Error copying text to clipboard:', error);
          // You can optionally show an error message to the user here
        });
    },
    exportJson() {
      let jsonObject = {};
      for (const field of this.fields) {
        jsonObject[field.name] = '';
      }
      jsonObject = JSON.stringify(jsonObject, null, 2)
      navigator.clipboard.writeText(jsonObject)
        .then(() => {
          this.showSnackBar("הועתק " + "json", "green");
          // You can optionally show a success message to the user here
        })
        .catch((error) => {
          console.error('Error export json to clipboard:', error);
          // You can optionally show an error message to the user here
        });
    },
    async getData() {
      try {
        this.fields = [];
        this.progressShow = true;
        this.allowSave = false;
        let token = localStorage.getItem("token");
        const tableJSON = JSON.stringify({ tableName: this.tableName.name });

        let api = process.env.VUE_APP_BASE_URL + "/user/get_table_properties";
        const res = await fetch(api, ApiServices.requestOptions("POST", tableJSON, token));
        const jsonObject = await res.json();
        this.progressShow = false;
        if (res.status >= 400) {
          this.showSnackBar(" שגיאה בקבלת נתונים" + jsonObject.message, "red");
        } else if (res.status === 200) {
          this.allowSave = true;
          this.fields = jsonObject.fieldsData;
          this.settings = jsonObject.settingsData;
          this.isMenuOpen = this.fields.map(() => false);
          this.workspaceId = jsonObject.workspaceId
        }
      } catch (error) {
        this.progressShow = false;
        this.showSnackBar("Error get items list: " + error, "red");
      }
    },
    isFieldNameValid(field) {
      return !field.includes('"');
    },
    showSnackBar(message, color) {
      this.snacbarText = message;
      this.snackbarColorBt = color;
      this.snackbar = true
    },
    addFiled() {
      const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
      let fieldName = '';
      for (let i = 0; i < 2; i++) {
        const randomIndex = Math.floor(Math.random() * chars.length);
        fieldName += chars.charAt(randomIndex);
      }
      fieldName += "_"
      this.fields.push({ name: fieldName, hebrew_name: "", allowNull: true, unique: false, show: true, type: "טקסט", list: [], linkedList: {}, coin: "" });
    },
    async saveChanges() {
      const fieldsJSON = JSON.stringify({ fields: this.fields, settings: this.settings, tableName: this.tableName });
      this.progressShow = true;
      let token = localStorage.getItem("token");
      try {
        let api = process.env.VUE_APP_BASE_URL + "/user/update_table_properties";
        const res = await fetch(api, ApiServices.requestOptions("PUT", fieldsJSON, token));
        this.progressShow = false;
        const jsonObject = await res.json();
        if (res.status >= 400) {
          console.log(res);
          this.showSnackBar(" שגיאה בעדכון שדות " + jsonObject.message, "red");
        } else if (res.status === 200) {
          this.showSnackBar("הפרטים נשמרו בהצלחה!", "green");
          setTimeout(() => {
            this.$emit('backToTableData', this.tableName)
          }, 800);
        }
      } catch (error) {
        this.progressShow = false;
        this.showSnackBar("Error saved fields: " + error, "red");

      }
    },
    backToTableData() {
      this.$emit('backToTableData', this.tableName)
    },
    openListField(item, index) {
      this.fieldEditList = item
      this.fieldIndex = index;
      this.listDialog = true;
    },
    openListLinkedField(item, index) {
      this.fieldEditListLinked = item
      this.fieldIndex = index;
      this.listLinkedDialog = true;
    },
    listUpdated(fieldListUpdated, index) {
      this.fields[index] = fieldListUpdated;
    },
    listLinkedUpdated(fieldListLinkedUpdated, index) {
      this.fields[index] = fieldListLinkedUpdated;
    },
    validateTableName(name) {
      // Add any validation logic you need
      return name.length >= 2; // Example: require at least 2 characters
    },
  },
  watch: {
    tableName: {
      handler() {
        this.getData()
      },
      immediate: true
    }
  },
  created() {
    let auth = Auth.checkAuth();
    if (!auth) {
      this.$router.replace({ name: "login" })
    }
  },
}
</script>
<style scoped>
/* Header Styles */
.header-section {
  background: white;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 2px 15px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  max-width: 1400px;
  margin: 40px auto;
}

.header-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

@media (min-width: 768px) {
  .header-content {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 16px 24px;
  }
}

.header-left {
  display: flex;
  align-items: center;
  width: 100%;
}

.header-right {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

@media (min-width: 768px) {
  .header-right {
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
    width: auto;
  }
}

.back-btn {
  background: rgba(13, 44, 109, 0.05);
  transition: all 0.3s ease;
}

.back-btn:hover {
  background: rgba(13, 44, 109, 0.1);
  transform: translateX(2px);
}

.table-name-section {
  flex: 1;
  max-width: 300px;
  margin: 0 16px;
}

.table-name-input {
  font-size: 1.25rem !important;
  font-weight: 600 !important;
}

.table-name-input>>>.v-input__slot {
  min-height: 40px !important;
  border: 1px solid rgba(13, 44, 109, 0.1) !important;
  transition: all 0.3s ease;
}

.table-name-input>>>.v-input__slot:hover {
  border-color: rgba(13, 44, 109, 0.2) !important;
  background: rgba(13, 44, 109, 0.02);
}

.table-name-input.v-input--is-focused>>>.v-input__slot {
  border-color: #0d2c6d !important;
  box-shadow: 0 0 0 1px rgba(13, 44, 109, 0.2) !important;
}

.api-name-section {
  background: #f8fafc;
  padding: 8px 12px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  border: 1px solid rgba(13, 44, 109, 0.1);
}

.api-name2-section {
  background: #f8fafc;
  padding: 8px 12px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  border: 1px solid rgba(13, 44, 109, 0.1);
}

@media (min-width: 768px) {
  .api-name-section {
    width: auto;
    max-width: 250px;
  }
}

.api-label {
  color: #64748b;
  font-size: 0.875rem;
  font-weight: 500;
  white-space: nowrap;
}

.api-value {
  font-family: 'Roboto Mono', monospace;
  font-size: 0.875rem;
  color: #0f172a;
  display: flex;
  align-items: center;
  gap: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.copy-btn {
  opacity: 0.6;
  transition: all 0.3s ease;
}

.copy-btn:hover {
  opacity: 1;
  background: rgba(13, 44, 109, 0.1);
}

.header-actions {
  display: flex;
  gap: 8px;
  width: 100%;
}

@media (min-width: 768px) {
  .header-actions {
    width: auto;
  }
}

.action-btn {
  text-transform: none;
  letter-spacing: 0;
  border-radius: 6px;
  transition: all 0.3s ease;
  height: 40px;
}

.action-text {
  margin-right: 4px;
}

.save-btn {
  background: linear-gradient(135deg, #0d2c6d 0%, #1a4ba5 100%) !important;
}

.save-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(13, 44, 109, 0.2) !important;
}

/* Main Content */
.main-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 20px 16px 40px;
}

@media (min-width: 768px) {
  .main-content {
    padding: 20px 24px 40px;
  }
}

/* Section Card Styling */
.section-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.section-card:hover {
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05), 0 2px 4px rgba(0, 0, 0, 0.05);
}

/* Settings Section */
.settings-section {
  margin-bottom: 24px;
}

.settings-header {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 20px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  padding-bottom: 16px;
}

@media (min-width: 768px) {
  .settings-header {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
}

.section-title-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
}

.section-icon {
  color: #0d2c6d;
}

.section-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #0f172a;
  margin: 0;
}

.add-field-btn {
  background: linear-gradient(135deg, #0d2c6d 0%, #1a4ba5 100%) !important;
  color: white;
  border-radius: 6px;
  text-transform: none;
  letter-spacing: 0;
  transition: all 0.3s ease;
}

.add-field-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(13, 44, 109, 0.2) !important;
}

.settings-toggles {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.toggle-btn {
  text-transform: none;
  letter-spacing: 0;
  border-radius: 6px;
  transition: all 0.3s ease;
}

.toggle-btn:hover {
  transform: translateY(-1px);
}

/* Fields Section */
.fields-section {
  margin-bottom: 40px;
}

.fields-header {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 20px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  padding-bottom: 16px;
}

@media (min-width: 768px) {
  .fields-header {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
}

.fields-count {
  background: #f8fafc;
  padding: 6px 12px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid rgba(13, 44, 109, 0.1);
}

.count-label {
  color: #64748b;
  font-size: 0.875rem;
}

.count-value {
  font-weight: 600;
  color: #0d2c6d;
  font-size: 0.875rem;
}

.fields-container {
  max-height: 80vh;
  overflow-y: auto;
  padding-right: 4px;
}

.fields-container::-webkit-scrollbar {
  width: 6px;
}

.fields-container::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.02);
  border-radius: 3px;
}

.fields-container::-webkit-scrollbar-thumb {
  background: rgba(13, 44, 109, 0.1);
  border-radius: 3px;
}

.fields-container::-webkit-scrollbar-thumb:hover {
  background: rgba(13, 44, 109, 0.2);
}

/* Field Card Styling */
.field-card {
  background: #f8fafc;
  border-radius: 8px;
  margin-bottom: 16px;
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.field-card:hover {
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05), 0 2px 4px rgba(0, 0, 0, 0.05);
  transform: translateY(-2px);
}

.field-header {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: rgba(13, 44, 109, 0.03);
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.field-drag {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: move;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.3s ease;
}

.field-drag:hover {
  background: rgba(13, 44, 109, 0.1);
}

.field-number {
  font-weight: 600;
  color: #64748b;
}

.field-type-badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
}

.field-content {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

@media (min-width: 1024px) {
  .field-content {
    flex-direction: row;
  }
}

.field-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

@media (min-width: 768px) {
  .field-main {
    flex-direction: row;
  }
}

.field-name-input>>>.v-input__slot {
  border: 1px solid rgba(13, 44, 109, 0.1) !important;
  transition: all 0.3s ease;
}

.field-name-input>>>.v-input__slot:hover {
  border-color: rgba(13, 44, 109, 0.2) !important;
  background: rgba(13, 44, 109, 0.02);
}

.field-name-input.v-input--is-focused>>>.v-input__slot {
  border-color: #0d2c6d !important;
  box-shadow: 0 0 0 1px rgba(13, 44, 109, 0.2) !important;
}

.field-actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

@media (min-width: 768px) {
  .field-actions {
    flex-direction: row;
    align-items: center;
  }
}

.action-group {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  gap: 8px;
}

@media (min-width: 768px) {
  .action-group {
    display: flex;
  }
}

.additional-actions {
  display: flex;
  justify-content: center;
  gap: 8px;
}

@media (min-width: 768px) {
  .additional-actions {
    justify-content: flex-start;
  }
}

.currency-btn {
  transition: all 0.3s ease;
}

.currency-btn:hover:not(:disabled) {
  background: rgba(13, 44, 109, 0.1);
}

.currency-menu {
  border-radius: 8px;
  overflow: hidden;
}

.selected-currency {
  background: rgba(13, 44, 109, 0.05);
}

.list-edit-btn,
.list-link-btn {
  transition: all 0.3s ease;
}

.list-edit-btn:hover,
.list-link-btn:hover {
  transform: scale(1.1);
  box-shadow: 0 2px 8px rgba(13, 44, 109, 0.2) !important;
}


/* Floating Save Button */
.floating-save-btn {
  z-index: 99;
  box-shadow: 0 4px 12px rgba(13, 44, 109, 0.3) !important;
  transition: all 0.3s ease;
}

.floating-save-btn:hover {
  transform: translateY(-4px);
  box-shadow: 0 6px 16px rgba(13, 44, 109, 0.4) !important;
}

/* Animations */
.field-list-enter-active,
.field-list-leave-active {
  transition: all 0.3s ease;
}

.field-list-enter,
.field-list-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

/* Drag and Drop */
.drag-ghost {
  opacity: 0.5;
  background: #f8fafc;
  border: 2px dashed #0d2c6d;
}

.drag-active {
  background: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 10;
}

/* RTL Support */
.v-application {
  direction: rtl;
}

/* Mobile Optimizations */
@media (max-width: 767px) {
  .main-content {
    padding-top: 160px;
  }

  .field-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .field-type-badge {
    margin-right: 0;
    align-self: flex-start;
  }

  .field-drag {
    width: 100%;
    justify-content: space-between;
  }

  .action-btn {
    padding: 0 8px;
    height: 32px;
  }

  .action-btn .v-icon {
    margin-right: 4px;
    font-size: 18px;
  }

  .api-value {
    max-width: calc(100vw - 150px);
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

/* Type Badge Colors */
.type-text {
  background: #e5e7eb;
  color: #374151;
}

.type-number {
  background: #dbeafe;
  color: #1e40af;
}

.type-date {
  background: #fef3c7;
  color: #92400e;
}

.type-time {
  background: #e0e7ff;
  /* כחול בהיר */
  color: #3730a3;
  /* כחול כהה */
}

.type-datetime {
  background: #ffedd5;
  color: #c2410c;
}

.type-list {
  background: #dcfce7;
  color: #166534;
}

.type-linked-list {
  background: #ccfbf1;
  color: #0f766e;
}

.type-link {
  background: #f3e8ff;
  color: #6b21a8;
}
</style>